import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { LOGOS, GATEWAY, BRAND_LIST, VIDEOS } from "../data/brands";
import { MaskedLines, Reveal } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import Footer from "../components/Footer";
import StatsSection from "../components/StatsSection";
import HeroVideoBg from "../components/HeroVideoBg";
import Leadership from "../components/Leadership";

export default function Gateway() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div className="bg-dejure-ivory">
      {/* Minimal top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/40 backdrop-blur-md py-5" data-testid="gateway-header">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
          <img src={LOGOS.group} alt="De Jure Group" className="h-12 md:h-16 w-auto" data-testid="gateway-logo" />
          <a href="#contact" data-testid="gateway-contact-cta" className="font-accent text-xs uppercase tracking-[0.2em] text-dejure-purple hover:text-dejure-purple-dark transition-colors duration-300">
            Contact
          </a>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="gateway-hero">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <HeroVideoBg src={VIDEOS.group.src} poster={VIDEOS.group.poster} />
          <div className="absolute inset-0 bg-gradient-to-t from-dejure-ivory via-dejure-ivory/55 to-dejure-purple-dark/25" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pb-24 md:pb-32 pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="font-accent text-xs uppercase tracking-[0.3em] text-dejure-purple"
          >
            {"United Arab Emirates"}
          </motion.p>
          <h1 className="mt-6 font-serif text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.86] tracking-tighter text-dejure-ink">
            <MaskedLines lines={GATEWAY.titleLines} delay={0.25} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 max-w-xl font-sans text-base md:text-lg leading-relaxed text-dejure-ink/70"
          >
            {GATEWAY.intro}
          </motion.p>
        </div>
      </section>

      {/* Brand gateway grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36" data-testid="gateway-brands">
        <Reveal>
          <div className="flex items-end justify-between border-b border-dejure-silver/30 pb-8">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-dejure-ink">
              Our Practices
            </h2>
            <span className="hidden md:block font-accent text-xs uppercase tracking-[0.2em] text-dejure-ink/40">
              Four disciplines · One name
            </span>
          </div>
        </Reveal>

        <div className="mt-4 grid md:grid-cols-2">
          {BRAND_LIST.map((b, i) => (
            <Reveal key={b.key} delay={i * 0.08}>
              <Link
                to={b.to}
                data-testid={`gateway-brand-${b.key}`}
                className="group relative flex flex-col justify-between min-h-[300px] md:min-h-[360px] border-b border-dejure-silver/30 p-8 md:p-12 md:odd:border-r hover:bg-dejure-mist transition-colors duration-500"
              >
                <div className="flex items-start justify-between">
                  <img src={LOGOS[b.key]} alt={b.name} className="h-16 md:h-20 w-auto transition-transform duration-500 group-hover:-translate-y-1" />
                  <ArrowUpRight className="text-dejure-silver transition-all duration-500 group-hover:text-dejure-purple group-hover:rotate-45" size={30} />
                </div>
                <div>
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tighter text-dejure-ink">{b.name}</h3>
                  <p className="mt-3 max-w-sm font-sans text-sm md:text-base text-dejure-ink/55">{b.blurb}</p>
                  <span className="mt-5 inline-block font-accent text-xs uppercase tracking-[0.2em] text-dejure-purple opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0">
                    Enter site →
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <Leadership />

      <StatsSection variant="light" />

      <ContactForm
        brand="group"
        heading="Speak with the Group"
        blurb="Whether legal, corporate, marketing or consulting, start here and we will direct your enquiry to the right practice."
      />
      <Footer brandKey="group" />
    </div>
  );
}
